import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Swal from "sweetalert2";
import Table from "../../core/pagination/datatable";
import { ErrorToaster } from "../components/toaster/Toaster";
import { Debounce, getDate } from "../../utils";
import { all_routes } from "../../Router/all_routes";
import DeliveryManagementServices from "../../services/DeliveryManagement";
import dayjs from "dayjs";
import { Calendar, PlusCircle } from "feather-icons-react/build/IconComponents";
import { Controller, useForm } from "react-hook-form";
import { DatePicker } from "antd";

const DeliveryManagement = () => {

  const routes = all_routes;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const { control, setValue } = useForm();

  // *For Loader
  const [loader, setLoader] = useState(false);

  // *For Deliveries
  const [deliveries, setDeliveries] = useState([]);
  const [deliveryDate, setDeliveryDate] = useState();
  const [orderIds, setOrderIds] = useState([]);

  // *Time Frame
  const timeFrame = [
    "8AM-12PM",
    "10AM-2PM",
    "10AM-2PM",
    "12PM-4PM",
    "12PM-4PM",
    "12PM-4PM",
    "2PM-6PM",
    "2PM-6PM",
    "2PM-6PM",
    "4PM-8PM",
    "4PM-8PM",
    "4PM-8PM",
    "LATE 6PM+",
    "LATE 6PM+",
    "LATE 6PM+",
  ];

  // *For Get Deliveries
  const getDeliveries = async () => {
    setLoader(true)
    try {
      let params = {
        delivery_date: searchParams.get('deliveryDate') ?? dayjs().format('YYYY-MM-DD')
      }
      setDeliveryDate(params.delivery_date)
      setValue('delivery_date', dayjs(params.delivery_date))
      const result = await DeliveryManagementServices.getDeliveries(params)
      const ids = result?.sales?.map((item) => item?.order_id)
      setOrderIds(ids)
      const deliveriesData = []
      const usedIndexes = new Set();
      timeFrame.forEach((item, index) => {
        const deliveryIndex = result?.sales?.findIndex(
          (time, i) => time.time_frame === item && !usedIndexes.has(i)
        );

        const deliveryTime = deliveryIndex !== -1 ? result.sales[deliveryIndex] : null;
        if (deliveryIndex !== -1) usedIndexes.add(deliveryIndex);
        deliveriesData.push({
          orderId: deliveryTime?.order_id || null,
          stopNumber: index + 1,
          timeFrame: item,
          estimatedDeliveryTime: deliveryTime?.estimatedDeliveryTime || "",
          orderNumber: deliveryTime?.order_number || "",
          Name: deliveryTime?.customer?.first_name ? deliveryTime?.customer?.first_name + " " + deliveryTime?.customer?.last_name : "",
          storeCode: deliveryTime?.store?.store_code || "",
          balance: deliveryTime?.due || "",
          deliveryType: deliveryTime?.delivery_type || "",
          city: deliveryTime?.customer?.city || "",
          salePerson: deliveryTime?.staff?.join(", ") || "",
          deliveryCharges: deliveryTime?.delivery_income || "",
          grandTotal: deliveryTime?.amount || "",
          comments: deliveryTime?.instructions || ""
        })
      })
      setDeliveries(deliveriesData)
    } catch (error) {
      // ErrorToaster(error)
      const deliveriesData = []
      timeFrame.forEach((item, index) => {
        deliveriesData.push({
          orderId: null,
          stopNumber: index + 1,
          timeFrame: item,
          estimatedDeliveryTime: "",
          orderNumber: "",
          Name: "",
          storeCode: "",
          balance: "",
          deliveryType: "",
          city: "",
          salePerson: "",
          deliveryCharges: "",
          grandTotal: "",
          comments: ""
        })
      })
      setDeliveries(deliveriesData)
    } finally {
      setLoader(false)
    }
  }

  const columns = [
    {
      title: "Stop Number",
      dataIndex: "stopNumber",
      align: "center",
      render: (text) => <span>{text}</span>,
    },
    {
      title: "Time Frame",
      dataIndex: "timeFrame",
      render: (text) => <span>{text || '-'}</span>,
    },
    {
      title: "Estimate Time",
      dataIndex: "estimatedDeliveryTime",
      render: (text) => <span>{text || '-'}</span>,
    },
    {
      title: "Order#",
      dataIndex: "orderNumber",
      render: (text) => <span>{text || '-'}</span>,
    },
    {
      title: "Name",
      dataIndex: "Name",
      render: (text) => <span>{text || '-'}</span>,
    },
    {
      title: "Store Code",
      dataIndex: "storeCode",
      render: (text) => <span>{text || '-'}</span>,
    },
    {
      title: "Balance",
      dataIndex: "balance",
      render: (text) => (
        <span>
          {text ? (
            <span className={text > 0 && "text-danger"}>
              {text > 0 ? (
                <b>
                  ${parseFloat(text).toFixed(2)}
                </b>
              ) : (
                `$${parseFloat(text).toFixed(2)}`
              )}
            </span>
          ) : ('-')}
        </span>
      ),
    },
    {
      title: "Payment Method",
      dataIndex: "deliveryType",
      render: (text) => <span>{text || '-'}</span>,
    },
    {
      title: "Delivery Type",
      dataIndex: "deliveryType",
      render: (text) => <span>{text || '-'}</span>,
    },
    {
      title: "City",
      dataIndex: "city",
      render: (text) => <span>{text || '-'}</span>,
    },
    {
      title: "Sale Person",
      dataIndex: "salePerson",
      render: (text) => <span>{text || '-'}</span>,
    },
    {
      title: "Delivery Charges",
      dataIndex: "deliveryCharges",
      render: (text) => <span>{text ? `$${parseFloat(text).toFixed(2)}` : '-'}</span>,
    },
    {
      title: "Grand Total",
      dataIndex: "grandTotal",
      render: (text) => <span>{text ? `$${parseFloat(text).toFixed(2)}` : '-'}</span>,
    },
    {
      title: "Comments",
      dataIndex: "comments",
      render: (text) => <span>{text || '-'}</span>,
    },
  ];

  useEffect(() => {
    getDeliveries()
  }, [searchParams]);

  return (
    <div>
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="add-item d-flex">
              <div className="page-title">
                <h4>Delivery Management</h4>
                <h6>Manage Your Delivery</h6>
              </div>
            </div>
            <div className="page-btn">
              <Link
                to={`${routes.viewDeliveryReceipt}?id=${orderIds.join(',')}`}
                className="btn btn-added"
              >
                <PlusCircle className="me-2" />
                Print Delivery Receipt
              </Link>
            </div>
          </div>
          <div className="card table-list-card">
            <div className="card-body">
              <div className="table-top justify-content-start align-items-center">
                <div className="input-blocks mb-0" style={{ width: '200px' }}>
                  <label>Delivery Date</label>
                  <div className="input-groupicon calender-input" >
                    <Calendar className="info-img" />
                    <Controller
                      name="delivery_date"
                      control={control}
                      render={({ field: { onChange, onBlur, value, ref } }) => (
                        <DatePicker
                          className="datetimepicker"
                          placeholder="Delivery Date"
                          format={'MM-DD-YYYY'}
                          onChange={(date) => { onChange(date || null); setSearchParams({ "deliveryDate": dayjs(date).format('YYYY-MM-DD') }) }}
                          onBlur={onBlur}
                          value={value ? dayjs(value, 'MM-DD-YYYY') : dayjs()}
                          ref={ref}
                        />
                      )}
                    />
                  </div>
                </div>
                <div className="page-title w-100 text-center">
                  <h2>{dayjs(deliveryDate).format('DD MMM YYYY')}</h2>
                </div>
                {/* <div className="search-set">
                  <div className="search-input">
                    <input
                      type="text"
                      placeholder="Search"
                      className="form-control form-control-sm formsearch"
                      onChange={(e) => handleFilter({ search: e.target.value })}
                    />
                    <Link to className="btn btn-searchset">
                      <i data-feather="search" className="feather-search" />
                    </Link>
                  </div>
                </div> */}
              </div>
              <div className="table-responsive">
                <Table
                  loading={loader}
                  columns={columns}
                  dataSource={deliveries}
                  isPagination={false}
                  onRow={(obj) => {
                    if (!obj?.orderId) return null
                    return {
                      onClick: () => {
                        navigate(`${routes.viewDeliveryReceipt}?id=${obj?.orderId}`);
                      },
                    };
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeliveryManagement;
